import { Sun, Wrench } from "lucide-react";
import serviceInstallation from "@/assets/service-installation.webp";
import serviceRepair from "@/assets/service-repair-before-after.webp";
import SectionDivider from "@/components/SectionDivider";
import SunRays from "@/components/SunRays";
import TiltCard from "@/components/TiltCard";

const services = [
  {
    icon: Sun,
    title: "Sun Screen Installation",
    description: "Custom-built solar screens for every window. Reduce heat by up to 90% and lower your energy bills.",
    image: serviceInstallation,
  },
  {
    icon: Wrench,
    title: "Screen Repair & Replacement",
    description: "Fast, affordable repairs for damaged or worn screens. We service all brands and styles.",
    image: serviceRepair,
  },
];

export default function ServicesSection() {
  return (
    <>
      <section id="services" className="py-12 md:py-28 bg-section-alt texture-swoosh texture-noise relative overflow-hidden">
        <SunRays variant="side" className="-left-20 top-1/2 -translate-y-1/2 w-[150px] h-[150px] md:w-[400px] md:h-[400px]" />

        <div className="absolute -top-10 -right-10 w-28 h-28 md:-top-20 md:-right-20 md:w-60 md:h-60 rounded-full border-[4px] md:border-[6px] border-primary/8 animate-spin-slow" />
        <div className="absolute -bottom-6 -left-6 w-20 h-20 md:-bottom-10 md:-left-10 md:w-40 md:h-40 rounded-full bg-sun-yellow/8" />

        <div className="container relative z-10">
          <div className="text-center mb-8 md:mb-14">
            <span className="inline-block bg-accent text-accent-foreground rounded-full px-4 py-1.5 font-bold text-sm mb-4 tilt-left comic-border-teal">
              Our Services
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-foreground mb-4 text-shadow-pop">
              What We <span className="text-gradient">Do Best</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-base md:text-lg">
              Professional sun screen solutions for homes and businesses across the Phoenix metro area.
            </p>
            <div className="shimmer-line max-w-xs mx-auto mt-6 rounded-full hidden md:block" />
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-3xl mx-auto">
            {services.map((service, i) => (
              <TiltCard
                key={service.title}
                className={`glass-card rounded-2xl md:rounded-3xl overflow-hidden comic-border-teal card-elite gradient-border group ${i % 2 === 0 ? 'md:tilt-left' : 'md:tilt-right'}`}
              >
                <div className="h-32 md:h-52 overflow-hidden relative gallery-overlay">
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${i === 0 ? 'object-[center_20%]' : ''}`}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                </div>
                <div className="p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-2 md:mb-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-accent flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="w-5 h-5 md:w-6 md:h-6 text-accent-foreground" />
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-foreground">{service.title}</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed text-sm md:text-base">{service.description}</p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>
      <SectionDivider variant="doublewave" fillClass="fill-background" />
    </>
  );
}
